import { useSelector, useDispatch } from 'react-redux'
import { 
    useSkipRefinementItemMutation, 
    useChooseRefinementCategoryMutation, 
    useChangePreviousChoiceMutation 
} from '../../../../../../../store/apis/rubric-refinement.api'
import { selectCategory, skipItem, revertItemToState } from '../../../../../../../store/slices/rubric-refinement.slice'
import { RefinementItemTypes } from '../constants'

/**
 * A comprehensive hook that manages both state and actions for the rubric refinement process.
 * Combines state derivation, business logic, and API calls in one place.
 * Makes components purely presentational by handling all refinement flow concerns.
 */
export const useRefinementFlow = () => {
    const dispatch = useDispatch()
    
    // ===============================
    // STATE MANAGEMENT
    // ===============================
    
    const refinementItems = useSelector(state => state.rubricRefinement.refinementItems)
    const refinementProcess = useSelector(state => state.rubricRefinement.refinementProcess)
    const hasStarted = useSelector(state => state.rubricRefinement.hasStarted)
    const isFinished = useSelector(state => state.rubricRefinement.isFinished)
    const processingAction = useSelector(state => state.rubricRefinement.processingAction)

    // Get the last item in the refinement list
    const lastItem = refinementItems.length > 0 ? refinementItems[refinementItems.length - 1] : null

    // Check for a pending API call related to the last item
    const isPendingApiCall = useSelector(state => {
        if (!lastItem) return false

        // List of mutations we want to track
        const pendingMutations = ['confirmSuggestion', 'chooseRefinementCategory', 'skipRefinementItem']

        return pendingMutations.some(
            mutation =>
                state.api.mutations?.[`${mutation}(${JSON.stringify({ refinementItemId: lastItem?.id })})`]?.status ===
                'pending',
        )
    })

    // We are waiting for a response if an action was taken and API is processing
    const isWaitingForResponse = processingAction || isPendingApiCall

    // We're finished when the backend tells us there are no more items
    const isAllItemsReviewed = isFinished && !isWaitingForResponse

    // Check if the last item has been acted upon (category selected, suggestion approved/rejected, or skipped)
    const isLastItemActedUpon =
        lastItem &&
        ((lastItem.type === RefinementItemTypes.SUBMISSION && (lastItem.selectedCategoryId || lastItem.isSkipped)) ||
            (lastItem.type === RefinementItemTypes.SUGGESTION &&
                (lastItem.diffReviewChoice === 'approved' ||
                    lastItem.diffReviewChoice === 'rejected' ||
                    lastItem.isSkipped)))

    // Show thinking message when processing an action but not finished
    const isWaitingForNextItem = isLastItemActedUpon && isWaitingForResponse && !isFinished

    // Check if we're currently showing a category choice or diff review
    const isShowingDiffReview =
        lastItem?.type === RefinementItemTypes.SUGGESTION && !lastItem.diffReviewChoice && !lastItem.isSkipped

    const isShowingCategorySelection =
        lastItem?.type === RefinementItemTypes.SUBMISSION && !lastItem.selectedCategoryId && !lastItem.isSkipped

    // Get items that are in the current editable batch (same group as the last item)
    const currentBatchItems = lastItem?.groupId !== undefined 
        ? refinementItems.filter(item => item.groupId === lastItem?.groupId)
        : []

    // Helper function to check if an item is editable
    const isItemEditable = (itemId) => {
        return currentBatchItems.some(item => 
            item.id === itemId && 
            item.type === RefinementItemTypes.SUBMISSION
        )
    }

    // Helper function to get the batch index for a specific item
    const getBatchIndex = (itemId) => {
        const item = refinementItems.find(item => item.id === itemId)
        if (!item || item.groupId === undefined) return null
        
        // Get all submission items in the same batch as this item
        const batchSubmissionItems = refinementItems.filter(refinementItem => 
            refinementItem.groupId === item.groupId && 
            refinementItem.type === RefinementItemTypes.SUBMISSION
        )
        
        // Find the index of this item within its batch (1-based)
        const index = batchSubmissionItems.findIndex(batchItem => batchItem.id === itemId)
        return index >= 0 ? index + 1 : null
    }

    const getBatchSize = (itemId) => {
        const item = refinementItems.find(item => item.id === itemId)
        if (!item || item.groupId === undefined) return null
        if (item.groupId === lastItem?.groupId) return 20
        
        return refinementItems.filter(refinementItem => refinementItem.groupId === item.groupId).length
    }

    // ===============================
    // ACTION HANDLERS
    // ===============================
    
    // API mutations
    const [skipRefinementItem] = useSkipRefinementItemMutation()
    const [chooseRefinementCategory] = useChooseRefinementCategoryMutation()
    const [changePreviousChoice] = useChangePreviousChoiceMutation()

    /**
     * Generic handler for optimistic updates with automatic rollback on error
     * @param {Object} config - Configuration object
     * @param {number} config.refinementItemId - The ID of the refinement item
     * @param {Function} config.optimisticAction - Redux action to dispatch optimistically
     * @param {Function} config.getCurrentAPI - Function that returns current item API call
     * @param {Function} config.getPreviousAPI - Function that returns previous item API call
     * @param {string} config.errorMessage - Error message for logging
     */
    const executeOptimisticAction = async ({
        refinementItemId,
        optimisticAction,
        getCurrentAPI,
        getPreviousAPI,
        errorMessage
    }) => {
        const isCurrentItem = lastItem?.id === refinementItemId
        
        // Capture previous state for potential rollback
        const targetItem = refinementItems.find(item => item.id === refinementItemId)
        const previousState = {
            selectedCategoryId: targetItem?.selectedCategoryId || null,
            isSkipped: targetItem?.isSkipped || false,
            diffReviewChoice: targetItem?.diffReviewChoice || null
        }

        // Optimistically update the UI
        dispatch(optimisticAction)

        try {
            // Execute the appropriate API call based on item type
            const apiCall = isCurrentItem ? getCurrentAPI() : getPreviousAPI()
            await apiCall.unwrap()
        } catch (error) {
            console.error(errorMessage, error)
            
            // Revert optimistic update on error
            dispatch(revertItemToState({ refinementItemId, previousState }))
        }
    }

    /**
     * Handles category selection for both current and previous items
     * @param {number} refinementItemId - The ID of the refinement item
     * @param {number} categoryId - The ID of the selected category
     */
    const handleCategorySelect = async (refinementItemId, categoryId) => {
        await executeOptimisticAction({
            refinementItemId,
            optimisticAction: selectCategory({ refinementItemId, categoryId }),
            getCurrentAPI: () => chooseRefinementCategory({ refinementItemId, categoryId }),
            getPreviousAPI: () => changePreviousChoice({ 
                cleaningStepId: refinementItemId, 
                userDecision: { pattern_id: categoryId } 
            }),
            errorMessage: 'Failed to handle category choice:'
        })
    }

    /**
     * Handles skipping items for both current and previous items
     * @param {number} refinementItemId - The ID of the refinement item to skip
     */
    const handleSkip = async (refinementItemId) => {
        await executeOptimisticAction({
            refinementItemId,
            optimisticAction: skipItem({ refinementItemId }),
            getCurrentAPI: () => skipRefinementItem({ refinementItemId }),
            getPreviousAPI: () => changePreviousChoice({ 
                cleaningStepId: refinementItemId, 
                userDecision: "Skip" 
            }),
            errorMessage: 'Failed to skip item:'
        })
    }
    
    return {
        refinementItems,
        refinementProcess,
        hasStarted,
        isFinished,
        isShowingCategorySelection,
        isShowingDiffReview,
        isWaitingForNextItem,
        isWaitingForResponse,
        isAllItemsReviewed,
        lastItem,
        currentBatchItems,
        isItemEditable,
        getBatchIndex,
        getBatchSize,
        handleCategorySelect,
        handleSkip,
    }
} 